import { Navigate } from "react-router-dom";
import Table, { ColumnsType } from "antd/es/table";
import { TaskListItemDto } from "../../services/client";
import { TaskStatusBadge } from "./components/TaskStatusBadge";
import { Button, Spin } from "antd";
import { LinkButton } from "../LinkButton";
import { useCursorPagination } from "../../hooks/useCursorPagination";
import { GcloudLinkButton } from "../o11y/GcloudLinkButton";
import { taskLogsUrl } from "../../util/gcloud";
import { isViewAsAdmin } from "../../util/viewAsAdmin";
import { formatDateTime } from "../../util/formatDate";

export type ListTasksProps = {
  pages?: { data: TaskListItemDto[]; page_size: number }[];
  isLoading?: boolean;
  error?: boolean;
  className?: string;
  getDetailsUrl?: (record: TaskListItemDto) => string;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  paramKey?: string;
};

export const ListTasks: React.FC<ListTasksProps> = ({
  pages,
  isLoading,
  error,
  className,
  getDetailsUrl,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  paramKey,
}) => {
  const { dataSource, pagination, isPageLoading } =
    useCursorPagination<TaskListItemDto>({
      pages,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
      paramKey,
    });

  const isAdmin = isViewAsAdmin();

  const columns: ColumnsType<TaskListItemDto> = [
    { title: "Task ID", dataIndex: "task_id", key: "task_id" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <TaskStatusBadge status={status} />,
    },
    {
      title: "Scheduled",
      dataIndex: "scheduled_at",
      key: "Scheduled",
      render: (date) => formatDateTime(date),
    },
  ];

  if (isAdmin) {
    columns.push({
      title: "",
      key: "logs",
      render: (_, record) => (
        <GcloudLinkButton href={taskLogsUrl(record.id)} size="small">
          Open logs
        </GcloudLinkButton>
      ),
    });
  }

  if (getDetailsUrl) {
    columns.push({
      title: "",
      render: (_, record) =>
        record.status === "DONE" ? (
          <LinkButton to={getDetailsUrl(record)} type="primary">
            Details
          </LinkButton>
        ) : (
          <Button disabled type="primary">
            Details
          </Button>
        ),
    });
  }

  if (isLoading && !error) {
    return (
      <div className={"flex justify-center items-center h-full" + className}>
        <Spin />
      </div>
    );
  }

  if (error || !pages) {
    return <Navigate to="/404" />;
  }

  return (
    <Table<TaskListItemDto>
      columns={columns}
      dataSource={dataSource}
      loading={isPageLoading}
      pagination={pagination}
      className={className}
      rowKey="id"
    />
  );
};
